// frontend/src/pages/admin/UsagePage.tsx
import { useEffect, useMemo, useState } from "react";
import { api, type AdminUsageRow } from "../../lib/api";
import {
  DEFAULT_SORT_DIR,
  DEFAULT_SORT_FIELD,
  PAGE_SIZE,
  type SortDir,
  type SortField,
  type UsageFilters,
  filterRows,
  paginateRows,
  sortRows,
} from "./usageHelpers";

// ── Formatters ────────────────────────────────────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatNum(n: number): string {
  return n.toLocaleString(undefined);
}

function formatRelative(s: string | null): string {
  if (!s) return "never";
  const dt = Date.now() - new Date(s).getTime();
  const min = 60 * 1000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (dt < min) return "just now";
  if (dt < hr) return `${Math.floor(dt / min)} min ago`;
  if (dt < day) return `${Math.floor(dt / hr)} h ago`;
  return `${Math.floor(dt / day)} d ago`;
}

// ── Sortable column header ────────────────────────────────────────────────────

interface SortHeaderProps {
  label: string;
  field: SortField;
  current: SortField;
  dir: SortDir;
  align?: "left" | "right";
  onSort: (field: SortField) => void;
}

function SortHeader({ label, field, current, dir, align = "left", onSort }: SortHeaderProps) {
  const active = field === current;
  const arrow = active ? (dir === "asc" ? "↑" : "↓") : "";
  const ariaSort = active ? (dir === "asc" ? "ascending" : "descending") : "none";
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"}`} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
      >
        {label}
        <span className="text-slate-400" aria-hidden="true">{arrow}</span>
      </button>
    </th>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/** Admin → Usage sub-page. Per-user table with artifact + Anthropic counts. */
export function UsagePage() {
  const [allRows, setAllRows] = useState<AdminUsageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<UsageFilters>({
    q: "",
    role: "all",
    status: "all",
  });

  const [sortField, setSortField] = useState<SortField>(DEFAULT_SORT_FIELD);
  const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_SORT_DIR);
  const [page, setPage] = useState(0);

  useEffect(() => {
    api.admin
      .usage()
      .then(setAllRows)
      .catch((e: Error) => setError(e.message));
  }, []);

  /** Toggle sort: same field flips direction; new field starts at desc. */
  function handleSort(field: SortField) {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(0);
  }

  function setFilter<K extends keyof UsageFilters>(key: K, value: UsageFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }

  // Derived pipeline: filter → sort → paginate
  const filtered = useMemo(
    () => (allRows ? filterRows(allRows, filters) : []),
    [allRows, filters],
  );
  const sorted = useMemo(
    () => sortRows(filtered, sortField, sortDir),
    [filtered, sortField, sortDir],
  );
  const pageRows = useMemo(
    () => paginateRows(sorted, page, PAGE_SIZE),
    [sorted, page],
  );
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);

  if (error) {
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
        {error}
      </p>
    );
  }
  if (allRows === null) return <p className="text-slate-500">Loading…</p>;
  if (allRows.length === 0) return <p className="text-slate-500">No users yet.</p>;

  const showFrom = sorted.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const showTo = Math.min((page + 1) * PAGE_SIZE, sorted.length);

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Usage</h1>
      <p className="mb-4 text-sm text-slate-500">
        Per-user activity. Anthropic counts come from the proxy
        <code className="mx-1 rounded bg-slate-100 px-1 text-xs">/api/anthropic/messages</code>;
        last MCP token use comes from the most-recent token activity.
      </p>

      {/* Filter bar */}
      <section className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search email or name…"
          value={filters.q}
          onChange={(e) => setFilter("q", e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        />
        <select
          value={filters.role}
          onChange={(e) => setFilter("role", e.target.value as UsageFilters["role"])}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilter("status", e.target.value as UsageFilters["status"])}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="all">Active + deactivated</option>
          <option value="active">Active only</option>
          <option value="deactivated">Deactivated only</option>
        </select>
      </section>

      {sorted.length === 0 ? (
        <p className="text-sm text-slate-500">No users match the current filters.</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <SortHeader label="User" field="email" current={sortField} dir={sortDir} onSort={handleSort} />
                  <SortHeader label="Artifacts" field="artifact_count" current={sortField} dir={sortDir} align="right" onSort={handleSort} />
                  <SortHeader label="Storage" field="total_bytes" current={sortField} dir={sortDir} align="right" onSort={handleSort} />
                  <SortHeader label="7-day req / tokens" field="tokens_7d" current={sortField} dir={sortDir} align="right" onSort={handleSort} />
                  <SortHeader label="30-day req / tokens" field="tokens_30d" current={sortField} dir={sortDir} align="right" onSort={handleSort} />
                  <SortHeader label="Last MCP use" field="last_token_use" current={sortField} dir={sortDir} align="right" onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {pageRows.map((r) => (
                  <tr key={r.email}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.email}</div>
                      <div className="flex gap-1.5 text-xs text-slate-500">
                        <span>{r.display_name}</span>
                        <span>·</span>
                        <span>{r.role}</span>
                        {!r.is_active && (
                          <>
                            <span>·</span>
                            <span className="text-slate-600">deactivated</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.artifact_count}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{formatBytes(r.total_bytes)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatNum(r.requests_7d)} / {formatNum(r.tokens_7d)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatNum(r.requests_30d)} / {formatNum(r.tokens_30d)}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">
                      {formatRelative(r.last_token_use)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination — only shown when there's more than one page */}
          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
              <span>
                Showing {showFrom}–{showTo} of {sorted.length}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-md border border-slate-300 px-3 py-1 text-xs disabled:opacity-40"
                >
                  ← Prev
                </button>
                <span className="px-2 py-1 text-xs">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded-md border border-slate-300 px-3 py-1 text-xs disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
